import { useTranslation } from 'react-i18next';

import { RelativeTime } from '@/shared/components/date';
import type { SearchResult, SearchResultHighlights } from '@/shared/registry/types';

import { PageIcon } from './bits';

/**
 * Global search hit of entity type `page` (meta: spaceName, spaceIcon, icon, version) — the content of the search page's
 * link row.
 */
export function WikiSearchResult({
  result,
  highlights,
}: {
  result: SearchResult;
  highlights?: SearchResultHighlights;
}) {
  const { t } = useTranslation('wiki');
  const meta = result.meta ?? {};
  const snippet = highlights?.snippet ?? result.snippet;
  return (
    <>
      <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md border bg-card text-base">
        <PageIcon icon={meta.icon ?? null} className="size-4 text-base" />
      </span>
      <span className="min-w-0 flex-1" data-testid="wiki-search-result">
        <span className="block truncate text-[0.9375rem] font-medium text-foreground">
          {highlights?.title ?? result.title}
        </span>
        <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
          <span>{t('search.page')}</span>
          {meta.spaceName && (
            <>
              <span aria-hidden="true">·</span>
              <span className="inline-flex min-w-0 items-center gap-1">
                {meta.spaceIcon && <span aria-hidden="true">{meta.spaceIcon}</span>}
                <span className="truncate">{meta.spaceName}</span>
              </span>
            </>
          )}
          {result.updatedAt && (
            <>
              <span aria-hidden="true">·</span>
              <RelativeTime date={result.updatedAt} />
            </>
          )}
        </span>
        {snippet && <span className="mt-1 line-clamp-2 block text-sm text-muted-foreground">{snippet}</span>}
      </span>
    </>
  );
}
